// Search types and constants for the frontend search UI.
import type { HelpView } from '@/types/help';

/** The six entity types included in the search index. */
export type EntityType = 'company' | 'fund' | 'fund-group' | 'investor' | 'contact' | 'security'

/** A single searchable entity returned by the search-index endpoint. */
export interface SearchIndexEntry {
  // "{entityType}:{entityId}", e.g. "company:abc-123"
  id: string
  entityType: EntityType
  entityId: string
  // primary display name shown in result row
  label: string
  // secondary context line, pre-computed
  subLabel: string
  // space-separated high-boost search tokens
  primary: string
  // space-separated low-boost search tokens
  secondary: string
  // extra IDs for client-side route building (e.g. { companyId } for securities)
  routing: Record<string, string>
}

/** Full payload returned by the searchIndex.getAll endpoint. */
export interface SearchIndexPayload {
  firmId: string
  entities: SearchIndexEntry[]
}

/** A single MiniSearch result with stored fields and scoring metadata. */
export interface SearchResultItem {
  id: string
  entityType: EntityType
  entityId: string
  label: string
  subLabel: string
  routing: Record<string, string>
  score: number
  // maps each matched query term to the fields it matched in
  match: Record<string, string[]>
}

/** A group of search results for a single entity type. */
export interface SearchGroup {
  type: EntityType
  label: string
  results: SearchResultItem[]
}

/** Display order for result groups in the search panel. */
export const GROUP_ORDER: EntityType[] = [
  'contact', 'fund', 'fund-group', 'company', 'investor', 'security',
]

/** Max results shown per group when no type filter is active. */
export const GROUP_CAPS: Record<EntityType, number> = {
  company: 5,
  fund: 4,
  'fund-group': 3,
  investor: 5,
  contact: 4,
  security: 3,
}

/** Human-readable plural labels for each entity type. */
export const GROUP_LABELS: Record<EntityType, string> = {
  company: 'Companies',
  fund: 'Funds',
  'fund-group': 'Fund groups',
  investor: 'Investors',
  contact: 'Contacts',
  security: 'Securities',
}

/** Human-readable plural labels for all item types (search + non-search). */
export const ITEM_TYPE_LABEL: Record<string, string> = {
  ...GROUP_LABELS,
  'mailing-list': 'Mailing lists',
  'custom-field': 'Custom fields',
  forecast: 'Forecasts',
}

/*
 * A row in global search that is not a record: an action that opens a wizard, a help page, or
 * (when no record matches) an action that creates a record named after the query.
 */
export type SearchTaskRow =
  | { kind: 'action'; actionId: string; label: string; subLabel: string }
  | { kind: 'create'; actionId: string; initialName: string; label: string; subLabel: string }
  | { kind: 'help'; view: HelpView; label: string; subLabel: string };

/** A labelled group of task rows ("Actions", "Help", "Create"), shown above the records. */
export interface SearchTaskGroup {
  label: string;
  rows: SearchTaskRow[];
}
